import { StateCreator } from 'zustand/index';
import { CalendarStore, initialCalendarView } from '@/hooks/use-calendar-store';
import { CalendarView } from '@/types/calendar-view';
import { RefObject } from 'react';
import FullCalendar from '@fullcalendar/react';
import { initialFilters } from '@/lib/slices/filter';
import { CursorPosition } from '@/hooks/use-realtime-cursors';
import { SchedulePermission } from '@/enums/permissions';

// Simple slot width value - no longer dynamic
const DEFAULT_SLOT_WIDTH = 46;

// Minimal Schedule type to avoid Prisma import issues - matches Prisma Schedule structure
interface Schedule {
  id: string;
  name: string | null;
  notes: string | null;
  logoUrl: string | null;
  logoUploadedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  shareEnabled: boolean;
  shareToken: string | null;
  sharePublishedAt: Date | null;
  shareFrom: string | null;
  shareTo: string | null;
  parentId: string | null;
  parentSubId: string | null;
  subscription: string;
  subscriptionEndAt: Date | null;
  printLogoUrl: string | null;
  printLogoUploadedAt: Date | null;

  [key: string]: unknown;
}

// EditEventInput interface for the store
export interface EditEventInput {
  id: string;
  title: string;
  description: string;
  start: Date | undefined;
  end: Date | undefined;
  color: string;
  resourceIds: string[];
  status: string;
  documents?: Array<{ id: string; url: string; name: string; size: number; type: string }>;
  activeCommentId?: string; // ID of the comment being edited
}

type ResourceEditPayload = {
  id: string | null;
  categoryId: string;
};
export type SharedStore = {
  schedule?: Schedule;
  cellWidth: number;
  calendarTitle: string;
  calendarStart?: Date;
  calendarEnd?: Date;
  permissions?: SchedulePermission[];
  setPermissions: (permissions: SchedulePermission[]) => void;
  cursors: CursorPosition[];
  setCursors: (cursors: CursorPosition[]) => void;
  setCellWidth: (width: number) => void;
  setSchedule: (schedule: Schedule | undefined) => void;
  calendarView: CalendarView;
  setScheduleName: (name: string) => void;
  setCalendarView: (view: CalendarView) => void;
  reset: () => void;
  switchToProjectView: () => void;
  calRef?: RefObject<FullCalendar>;
  switchToResourceView: () => void;
  expanded: Map<string, boolean>;
  toggleExpand: (id: string) => void;
  currentViewType: string;
  setCurrentViewType: (viewType: string) => void;
  slotWidth: number;
  openTimeOff: boolean;
  setOpenTimeOff: (o: boolean) => void;
  shareToken?: string;
  setShareToken: (token: string) => void;
  displayFrom?: string;
  displayTo?: string;
  setDisplayFrom: (from: string) => void;
  setDisplayTo: (to: string) => void;
  setCalendarStart: (start: Date) => void;
  setCalendarEnd: (end: Date) => void;
  setCalendarTitle: (title: string) => void;
  sharedResourceId?: string;
  setSharedResourceId: (id: string | undefined) => void;
  sharedProjectId?: string;
  setSharedProjectId: (id: string | undefined) => void;
  editResource?: ResourceEditPayload;
  setEditResource: (edit: ResourceEditPayload | undefined) => void;
  adminLocked: boolean;
  setAdminLocked: (locked: boolean) => void;
  toggleAdminLock: () => void;
  editProjectId?: string;
  setEditProjectId: (id: string | undefined) => void;
  editResourceNote?: string;
  setEditResourceNote: (id: string | undefined) => void;
  editProjectNote?: string;
  setEditProjectNote: (name: string | undefined) => void;
  editEventObject?: EditEventInput;
  setEditEventObject: (event: EditEventInput | undefined) => void;
  deleteEventId?: string;
  setDeleteEventId: (id: string | undefined) => void;
};

export const createSharedSlice: StateCreator<CalendarStore, [], [], SharedStore> = (set, get) => ({
  schedule: undefined,
  calendarView: initialCalendarView,
  calendarTitle: '',
  calendarStart: undefined,
  calendarEnd: undefined,
  currentViewType: 'resourceTimeline6w', // Default view type
  slotWidth: DEFAULT_SLOT_WIDTH,
  calRef: { current: null },
  expanded: new Map<string, boolean>(),
  openTimeOff: false,
  cellWidth: 45,
  cursors: [],
  permissions: [],
  sharedResourceId: undefined,
  sharedProjectId: undefined,
  editResource: undefined,
  adminLocked: true, // Default to locked for safety
  editProjectId: undefined,
  deleteEventId: undefined,
  editResourceNote: undefined,
  editProjectNote: undefined,
  editEventObject: undefined,
  setCursors: (cursors) => {
    set((state) => ({
      ...state,
      cursors,
    }));
  },
  setCellWidth: (o) => {
    set((state) => ({
      ...state,
      cellWidth: o,
    }));
  },
  setOpenTimeOff: (o) => {
    set((state) => ({
      ...state,
      openTimeOff: o,
    }));
  },
  setSchedule: (schedule) => {
    set((state) => ({
      ...state,
      schedule,
    }));
  },
  switchToProjectView: () => {
    set((state) => ({
      ...state,
      calendarView: 'projects',
    }));
  },
  switchToResourceView: () => {
    set((state) => ({
      ...state,
      calendarView: 'resources',
    }));
  },
  setScheduleName: (name) => {
    set((state) => ({
      ...state,
      scheduleName: name,
    }));
  },
  setCalendarView: (view) => {
    set((state) => ({
      ...state,
      calendarView: view,
    }));
  },
  setShareToken: (token: string) => {
    set((state) => ({
      ...state,
      shareToken: token,
    }));
  },
  toggleExpand: (id) => {
    const { expanded } = get();
    const previousValue = expanded.get(id);
    expanded.set(id, !previousValue);
    set((state) => ({
      ...state,
      expanded: new Map(expanded),
    }));
  },
  setDisplayFrom: (from: string) => {
    set((state) => ({
      ...state,
      displayFrom: from,
    }));
  },
  setDisplayTo: (to: string) => {
    set((state) => ({
      ...state,
      displayTo: to,
    }));
  },
  reset: () => {
    set((state) => ({
      ...state,
      calendarView: initialCalendarView,
      requirements: [],
      slotWidth: DEFAULT_SLOT_WIDTH,
      expanded: new Map<string, boolean>(),
      filters: initialFilters,
    }));
  },
  setCurrentViewType: (viewType: string) => {
    set((state) => ({
      ...state,
      currentViewType: viewType
    }));
  },
  setCalendarStart: (start: Date) => {
    set((state) => ({
      ...state,
      calendarStart: start,
    }));
  },
  setCalendarEnd: (end: Date) => {
    set((state) => ({
      ...state,
      calendarEnd: end,
    }));
  },
  setCalendarTitle: (title: string) => {
    set((state) => ({
      ...state,
      calendarTitle: title,
    }));
  },
  setSharedResourceId: (id: string | undefined) => {
    set((state) => ({
      ...state,
      sharedResourceId: id,
    }));
  },
  setSharedProjectId: (id: string | undefined) => {
    set((state) => ({
      ...state,
      sharedProjectId: id,
    }));
  },
  setEditResource: (edit: ResourceEditPayload | undefined) => {
    set((state) => ({
      ...state,
      editResource: edit,
    }));
  },
  setAdminLocked: (locked: boolean) => {
    set((state) => ({
      ...state,
      adminLocked: locked,
    }));
  },
  toggleAdminLock: () => {
    set((state) => ({
      ...state,
      adminLocked: !state.adminLocked,
    }));
  },
  setEditProjectId: (id) => {
    set((state) => ({
      ...state,
      editProjectId: id,
    }));
  },
  setEditResourceNote: (id: string | undefined) => {
    set((state) => ({
      ...state,
      editResourceNote: id,
    }));
  },
  setEditProjectNote: (name: string | undefined) => {
    set((state) => ({
      ...state,
      editProjectNote: name,
    }));
  },
  setEditEventObject: (event: EditEventInput | undefined) => {
    set((state) => ({
      ...state,
      editEventObject: event,
    }));
  },
  setPermissions: (permissions: SchedulePermission[]) => {
    set((state) => ({
      ...state,
      permissions,
    }));
  },
  setDeleteEventId: (id: string | undefined) => {
    set((state) => ({
      ...state,
      deleteEventId: id,
    }));
  }
});
